import { CheckCircle, Globe } from 'lucide-react';
import React, { useMemo } from 'react';

import { parseCareerDescription, type CareerContentBlock } from '../../utils/careerDescriptionParser';

interface CareerJobBodyProps {
  description: string | null;
  requirements: string[] | null;
  niceToHave: string[] | null;
  benefits: string[] | null;
}

function ListSection({
  title,
  items,
  icon: Icon = CheckCircle,
  iconClass = 'text-[#15803d]',
}: {
  title: string;
  items: string[];
  icon?: React.FC<{ className?: string }>;
  iconClass?: string;
}) {
  return (
    <section className="careers-job-section">
      <h2 className="home-problem-section__life-title careers-job-section__title">{title}</h2>
      <ul className="careers-job-list">
        {items.map((item, i) => (
          <li key={`${title}-${i}`} className="careers-job-list__item">
            <Icon className={`careers-job-list__icon ${iconClass}`} aria-hidden />
            <span>{item}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function normalizeSectionTitle(title: string): string {
  return title.toLowerCase().replace(/:+\s*$/, '').trim();
}

function parsedCoversSection(parsed: CareerContentBlock[], patterns: RegExp[]): boolean {
  return parsed.some(
    (block) =>
      block.type !== 'paragraph' &&
      patterns.some((pattern) => pattern.test(normalizeSectionTitle(block.title))),
  );
}

function renderParsedBlock(block: CareerContentBlock, idx: number) {
  if (block.type === 'paragraph') {
    return (
      <p key={idx} className="home-problem-section__subtitle careers-job-paragraph !mx-0 !max-w-none text-left">
        {block.text}
      </p>
    );
  }

  if (block.type === 'prose-section') {
    return (
      <section key={idx} className="careers-job-section">
        <h2 className="home-problem-section__life-title careers-job-section__title">{block.title}</h2>
        <div className="space-y-3 pl-1">
          {block.paragraphs.map((p, pi) => (
            <p key={pi} className="home-problem-section__subtitle careers-job-paragraph !mx-0 !max-w-none text-left !mt-0">
              {p}
            </p>
          ))}
        </div>
      </section>
    );
  }

  return <ListSection key={idx} title={block.title} items={block.items} />;
}

const CareerJobBody: React.FC<CareerJobBodyProps> = ({
  description,
  requirements,
  niceToHave,
  benefits,
}) => {
  const parsed = useMemo(
    () => (description ? parseCareerDescription(description) : []),
    [description],
  );

  const hasStructuredArrays =
    (requirements?.length ?? 0) > 0 ||
    (niceToHave?.length ?? 0) > 0 ||
    (benefits?.length ?? 0) > 0;

  const showRequirements =
    (requirements?.length ?? 0) > 0 &&
    !parsedCoversSection(parsed, [/requirement|must[\s-]?have|qualification/i]);

  const showNiceToHave =
    (niceToHave?.length ?? 0) > 0 &&
    !parsedCoversSection(parsed, [/nice[\s-]?to[\s-]?have/i]);

  const showBenefits =
    (benefits?.length ?? 0) > 0 &&
    !parsedCoversSection(parsed, [/benefit|perk/i]);

  if (!description && !hasStructuredArrays) return null;

  return (
    <div className="home-problem-section__mesh careers-job-body-mesh">
      <div className="hiw-mesh-layer" aria-hidden />
      <div className="home-problem-section__mesh-inner careers-job-body-inner">
        <div className="careers-job-body space-y-8">
          {parsed.length > 0 ? (
            parsed.map(renderParsedBlock)
          ) : (
            description && (
              <section className="careers-job-section">
                <h2 className="home-problem-section__life-title careers-job-section__title">About the Role</h2>
                <p className="home-problem-section__subtitle careers-job-paragraph !mx-0 !max-w-none text-left whitespace-pre-wrap">
                  {description}
                </p>
              </section>
            )
          )}

          {showRequirements && requirements && (
            <ListSection title="Requirements" items={requirements} />
          )}
          {showNiceToHave && niceToHave && (
            <ListSection title="Nice to Have" items={niceToHave} icon={Globe} iconClass="text-[#0ea5e9]" />
          )}
          {showBenefits && benefits && (
            <ListSection title="Benefits & Perks" items={benefits} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CareerJobBody;
